import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import { Simulation, Message } from '../lib/types';
import posthog from '../lib/posthog';
import { useRealtimeSync } from '../lib/hooks/useRealtimeSync';

interface SimulationContextType {
  simulations: Simulation[];
  setSimulations: React.Dispatch<React.SetStateAction<Simulation[]>>;
  createSimulation: (
    simulation: Omit<
      Simulation,
      'id' | 'created_at' | 'updated_at' | 'user_id'
    >,
  ) => Promise<Simulation>;
  updateSimulation: (
    id: string,
    simulation: Partial<Simulation>,
    options?: {
      requireStatus?: Simulation['status'];
      requireUpdatedAt?: string;
      requireGenerationId?: string;
    },
  ) => Promise<Simulation | null>;
  deleteSimulation: (id: string) => Promise<void>;
  deleteAllSimulations: () => Promise<void>;
  fetchSimulation: (id: string) => Promise<Simulation | null>;
  fetchSimulationMessages: (id: string) => Promise<Message[]>;
  loading: boolean;
  error: string | null;
}

interface FetchSimulationsOptions {
  showLoading?: boolean;
}

const SimulationContext = createContext<SimulationContextType | undefined>(
  undefined,
);

export function SimulationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const workspaceId = user?.currentWorkspace?.workspace_id;

  const fetchSimulations = async ({
    showLoading = true,
  }: FetchSimulationsOptions = {}) => {
    try {
      if (!user) {
        setSimulations([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (!workspaceId) {
        if (user.workspace_profiles == null) {
          setError(null);
          setLoading(true);
          return;
        }

        throw new Error('Workspace ID missing from user');
      }

      if (showLoading) {
        setLoading(true);
      }
      const { data, error } = await supabase
        .from('simulations')
        .select(
          `
            *,
            agent:agents(*),
            persona:personas(*)
          `,
        )
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setError(null);
      setSimulations(data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch simulations',
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSimulations({ showLoading: true });
  }, [user, workspaceId]);

  useRealtimeSync('simulations', workspaceId, () =>
    fetchSimulations({ showLoading: false }),
  );

  const fetchSimulation = async (simulationId: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('simulations')
        .select(
          `
            *,
            agent:agents(*),
            persona:personas(*)
          `,
        )
        .eq('id', simulationId)
        .eq('workspace_id', workspaceId) // TODO: We probably don't need this here. Remove and check.
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.[0] || null;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch simulation',
      );
    }
  };

  const fetchSimulationMessages = async (
    simulationId: string,
  ): Promise<Message[]> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('simulations')
        .select('messages')
        .eq('id', simulationId)
        .eq('workspace_id', workspaceId) // TODO: We probably don't need this here. Remove and check.
        .single();

      if (error) throw error;

      return (data?.messages as Message[]) || [];
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch simulation messages',
      );
      return [];
    }
  };

  const createSimulation = async (
    simulationData: Omit<
      Simulation,
      'id' | 'created_at' | 'updated_at' | 'user_id'
    >,
  ) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { data, error } = await supabase
        .from('simulations')
        .insert([
          { ...simulationData, user_id: user?.id, workspace_id: workspaceId },
        ])
        .select(
          `
          *,
          agent:agents(*),
          persona:personas(*)
        `,
        )
        .single();

      if (error) throw error;

      posthog.capture('simulation_created', {
        simulation_id: data.id,
        agent_id: data.agent_id,
        persona_id: data.persona_id,
        workspace_id: workspaceId,
      });

      setSimulations((current) => [data, ...current]);
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to create simulation',
      );
    }
  };

  const updateSimulation = async (
    id: string,
    simulationData: Partial<Simulation>,
    options?: {
      requireStatus?: Simulation['status'];
      requireUpdatedAt?: string;
      requireGenerationId?: string;
    },
  ): Promise<Simulation | null> => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      let query = supabase
        .from('simulations')
        .update(simulationData)
        .eq('id', id)
        .eq('workspace_id', workspaceId);

      if (options?.requireStatus) {
        query = query.eq('status', options.requireStatus);
      }

      if (options?.requireUpdatedAt) {
        query = query.eq('updated_at', options.requireUpdatedAt);
      }
      if (options?.requireGenerationId) {
        query = query.eq('generation_id', options.requireGenerationId);
      }

      const isConditional = Boolean(
        options?.requireStatus || options?.requireUpdatedAt,
      );

      const selectQuery = query.select(
        `
          *,
          agent:agents(*),
          persona:personas(*)
        `,
      );

      const { data, error } = isConditional
        ? await selectQuery.maybeSingle()
        : await selectQuery.single();

      if (error) throw error;
      if (!data) return null;

      posthog.capture('simulation_updated', {
        simulation_id: id,
        agent_id: data.agent_id,
        persona_id: data.persona_id,
        workspace_id: workspaceId,
      });

      setSimulations((current) =>
        current.map((simulation) =>
          simulation.id === id ? { ...simulation, ...data } : simulation,
        ),
      );
      return data;
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to update simulation',
      );
    }
  };

  const deleteSimulation = async (id: string) => {
    try {
      if (!user) throw new Error('User not found');
      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { error } = await supabase
        .from('simulations')
        .delete()
        .eq('id', id)
        .eq('workspace_id', workspaceId);

      if (error) throw error;
      posthog.capture('simulation_deleted', {
        simulation_id: id,
        workspace_id: workspaceId,
      });

      setSimulations((current) =>
        current.filter((simulation) => simulation.id !== id),
      );
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete simulation',
      );
    }
  };

  const deleteAllSimulations = async () => {
    try {
      if (!user) throw new Error('User not found');

      const workspaceId = user?.currentWorkspace?.workspace_id;
      if (!workspaceId) throw new Error('Workspace ID missing from user');

      const { error } = await supabase
        .from('simulations')
        .delete()
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      posthog.capture('simulation_deleted_all', {
        workspace_id: workspaceId,
      });

      setSimulations([]);
    } catch (err) {
      posthog.captureException(err);
      throw new Error(
        err instanceof Error ? err.message : 'Failed to delete all simulations',
      );
    }
  };

  return (
    <SimulationContext.Provider
      value={{
        simulations,
        setSimulations,
        createSimulation,
        updateSimulation,
        deleteSimulation,
        deleteAllSimulations,
        fetchSimulation,
        fetchSimulationMessages,
        loading,
        error,
      }}
    >
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulations() {
  const context = useContext(SimulationContext);
  if (context === undefined) {
    throw new Error('useSimulations must be used within a SimulationProvider');
  }
  return context;
}
